import React, { useRef, useState, useEffect } from "react";
import { Button, Typography, makeStyles, CircularProgress } from "@material-ui/core";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import axios from "axios";
import ErpToErpConfigurationSection from "../../shared/ErpToErpConfigurationSection";
import { DEFAULT_PRODUCT_TYPES } from "../../shared/erpToErpConfigData";
import useProductTypeNames from "../../shared/useProductTypeNames";
import { appUrl } from "../../../../../../../utils/config";
import { useApiBaseUrl } from "../../../CommonMethods/CommonMethods";

const useStyles = makeStyles((theme) => ({
  button: {
    minWidth: 180,
    borderRadius: 30,
  },
  backButton: {
    minWidth: 180,
    borderRadius: 30,
    background: "#707070",
    color: "#fff",
    marginRight: theme.spacing(2),
  },
}));

const ErpToErpConfigurations = (props) => {
  const {
    updateStepData,
    handleNext,
    handleBack,
    formData,
    hideStepActions = false,
    selectedCMOFormData,
  } = props;
  const classes = useStyles();
  const sectionRef = useRef(null);
  const productTypeNamesById = useProductTypeNames();
  const API_BASE_URL = useApiBaseUrl(appUrl);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const [brandOwnerMosymphonyID, setBrandOwnerMosymphonyID] = useState("");
  const token = localStorage.getItem("authToken");
  useEffect(() => {
    if (!token) return;
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join("")
      );
      const payload = JSON.parse(jsonPayload);
      setBrandOwnerMosymphonyID(payload.organization_id);
    } catch (e) {
      console.error("Invalid token", e);
    }
  }, [token]);

  // viewCMODetails nests the actual CMO/vendor config one level deeper
  // (vendorPlantDetails.vendorPlantDetails) than the flat shape this step
  // originally assumed — support both so this works regardless of caller.
  const vendorDetails =
    selectedCMOFormData?.vendorPlantDetails?.vendorPlantDetails ||
    selectedCMOFormData?.vendorPlantDetails ||
    {};
  const activatedProductTypeConfigs = (vendorDetails.vendorProductTypeConfigs || []).filter(
    (pt) => pt.activated === true
  );
  const productTypes =
    activatedProductTypeConfigs.length > 0
      ? activatedProductTypeConfigs.map((pt) => ({
          productTypeID: pt.productTypeID,
          productTypeName: pt.productTypeName || productTypeNamesById[pt.productTypeID] || "",
        }))
      : DEFAULT_PRODUCT_TYPES;

  const cmoName = selectedCMOFormData?.cmoDetails?.cmoName || "";

  // Real erpConfigurations (productTypeList/idocConfigList — see
  // ErpToErpConfigurationSection's deriveProductTypeConfigsFromRaw/deriveIdocFieldsFromRaw)
  // from /viewCMODetails, for a pending CMO resuming into this step. formData.erpToErpConfig
  // (this step's own internal-shape onChange output) only exists once the user has visited
  // this step in the current session — for a first visit it's still the stepper's default
  // {}, so without this, Product Type ERP-to-ERP Connectivity/Modules and IDoc
  // Configurations would silently show blank/default even though the CMO already has saved
  // real connectivity. Spread order lets formData's own internal shape win once present.
  const rawErpConfigurations = vendorDetails.erpConfigurations || {};

  // The CMO is created back in step 1 (CMO Details); its vendorMosymphonyID isn't
  // threaded through formData in this stepper, so fall back to the sessionStorage
  // snapshot the other steps (CMODetails, SiteDetails, PurchasingDetails, ...) already
  // stash it in.
  const vendorMosymphonyID =
    selectedCMOFormData?.vendorPlantDetails?.vendorMosymphonyID ||
    JSON.parse(sessionStorage.getItem("cmoDetailsResponse") || "{}")?.organizationId;

  const handleNextClick = async () => {
    if (!sectionRef.current) {
      handleNext();
      return;
    }
    const stepFormData = sectionRef.current.getFormData();
    const payload = sectionRef.current.getPayload();

    if (!API_BASE_URL) {
      updateStepData(stepFormData);
      handleNext();
      return;
    }

    setSaving(true);
    setSaveError("");
    // console.log("Saving ERP-to-ERP configuration with payload:", payload);
    // return
    try {
      await axios.post(`${API_BASE_URL}/updateVendorPlantDetails`, payload);
      updateStepData(stepFormData);
      handleNext();
    } catch (err) {
      console.error("Error updating ERP-to-ERP configuration:", err);
      setSaveError("Failed to save ERP-to-ERP Configurations. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cmo-stepper-wrapper">
      <div className="stepper-container">
        <ErpToErpConfigurationSection
          ref={sectionRef}
          productTypes={productTypes}
          editMode
          initialData={{ ...rawErpConfigurations, ...(formData || {}) }}
          cmoName={cmoName}
          vendorMosymphonyID={vendorMosymphonyID}
          brandOwnerMosymphonyID={brandOwnerMosymphonyID}
          onChange={(data) => updateStepData(data)}
        />
        {saveError && (
          <Typography color="error" style={{ marginTop: 16 }}>
            {saveError}
          </Typography>
        )}
        {!hideStepActions && (
          <div
            className="stepper-navigation-btns"
            style={{ display: "flex", justifyContent: "space-between", marginTop: 32 }}
          >
            <Button
              startIcon={<ArrowBackIosIcon />}
              variant="outlined"
              color="default"
              onClick={handleBack}
              className={classes.backButton}
              disabled={saving}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleNextClick}
              className={classes.button}
              endIcon={saving ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIosIcon />}
              disabled={saving}
            >
              {saving ? "Saving..." : "Next"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErpToErpConfigurations;
